/**
 * Die Alltag-Karten im Chat: Wortlaut, Aufnahme, Suche. (15.10.2026)
 *
 * Martin, mit zwei Karten aus dem Chat: *„Kann man bitte endlich diese
 * Cards überarbeiten? Es ist so anstrengend!“* — eine Liste, in der jeder
 * Eintrag Titel UND Datum derselben Aufnahme trug (neunmal, Karte um
 * Karte), und „Alltag-Wortlaut · 49-mal“ mit einem Block „Text“, in dem
 * der VOLLE Wortlaut einer Aufnahme stand: zehntausende Zeichen, Fremdtext
 * und Privates, mitten im Chat.
 *
 * Beide Werkzeuge hatten keine eigene Karte; die generische zeigte jedes
 * Feld, das einen Namen hat — und `text` heißt „Text“.
 *
 * Die Regel dieser Karten:
 *
 *   - Ein Wortlaut erscheint NIE auf dem Schirm. Er ist für das Modell. Die
 *     Karte sagt eine Zeile: „Wortlaut gelesen · Aufnahme 13.10. 17:58 ·
 *     8.400 Zeichen“ — Zeit aus den Feldern, Länge als Zahl.
 *   - Eine Liste von Aufnahmen: EIN Eintrag = EINE Zeile („13.10.2026 ·
 *     17:58“), die Länge rechts; Titel und Datum nie doppelt; höchstens
 *     ALLTAG_LISTE_MAX sichtbar, dann „und N weitere“.
 *   - Nichts zu zeigen: eine leise Zeile, kein Rohdaten-Knopf.
 *
 * Die Felder kommen aus tools/alltag-wortlaut.ts (`aufgenommen`
 * „13.10.2026, 17:58:42“, `tag` „2026-10-13“, `laenge_zeichen`) und
 * tools/alltag.ts (`entries[].title` „Transkript: Aufnahme 13.10.2026,
 * 17:58:42“, `date`, `zeichen_gesamt`). Im Verlauf ist der Wortlaut durch
 * WORTLAUT_PLATZHALTER ersetzt (memory/metadaten-kappen.ts) — die Karte
 * liest ihn ohnehin nicht.
 */
import { WORTLAUT_PLATZHALTER } from "../../memory/metadaten-kappen.js";
import { menschlicherFehler } from "./fehler-worte.js";
import { baueKarte, chips, fehlerBlock, kartenTitel, leerHinweis, liste, listenEintrag, weitereHinweis } from "./karte.js";
import { auspacken } from "./schritte.js";
import { werkzeugName } from "./werkzeug-namen.js";

/** So viele Aufnahmen zeigt eine Alltag-Liste, dann „und N weitere“ — streng, ohne die Toleranz von kuerzeListe. */
export const ALLTAG_LISTE_MAX = 5;

/** Die Werkzeuge, die hier ihre Karte bekommen. */
export const ALLTAG_WERKZEUGE = new Set(["alltag_lesen", "alltag_detail", "alltag_search"]);

/** „13.10.2026, 17:58:42“ oder „Transkript: Aufnahme 13.10.2026, 17:58:42“ → Datum und Uhrzeit (ohne Sekunden). */
const ZEIT = /(\d{1,2})\.(\d{1,2})\.(\d{4})(?:,?\s*(\d{1,2}:\d{2}))?/;
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface AufnahmeZeit {
  /** „13.10.2026“ */
  datum: string;
  /** „13.10.“ */
  kurz: string;
  /** „17:58“ oder leer */
  zeit: string;
}

/** Die Zeit einer Aufnahme aus den Feldern — erst der Titel/`aufgenommen`, dann der ISO-Tag. */
export function aufnahmeZeit(...quellen: unknown[]): AufnahmeZeit | null {
  for (const q of quellen) {
    if (typeof q !== "string") continue;
    const m = ZEIT.exec(q);
    if (m) {
      const [, tt = "", mm = "", jjjj = "", zeit = ""] = m;
      const t = tt.padStart(2, "0");
      const mo = mm.padStart(2, "0");
      return { datum: `${t}.${mo}.${jjjj}`, kurz: `${t}.${mo}.`, zeit: zeit.length === 4 ? `0${zeit}` : zeit };
    }
    const i = ISO.exec(q.trim());
    if (i) return { datum: `${i[3]}.${i[2]}.${i[1]}`, kurz: `${i[3]}.${i[2]}.`, zeit: "" };
  }
  return null;
}

/** „13.10.2026 · 17:58“ — die eine Zeile für eine Aufnahme. */
function aufnahmeZeile(z: AufnahmeZeit | null): string {
  if (!z) return "Aufnahme";
  return z.zeit ? `${z.datum} · ${z.zeit}` : z.datum;
}

const zahl = (n: number): string => n.toLocaleString("de-DE");

function zeichenText(n: unknown): string {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? `${zahl(n)} Zeichen` : "";
}

/** Zeichen eines Textfeldes — der Platzhalter des Verlaufs zählt nicht. */
function laengeVon(text: unknown): number {
  if (typeof text !== "string" || text === WORTLAUT_PLATZHALTER) return 0;
  return [...text].length;
}

/** Trefferstellen eines Eintrags („…eins…\n\n…zwei…“, tools/alltag.ts) — als Zahl, nie als Text. */
function stellenText(content: unknown): string {
  const stellen = typeof content === "string" && content !== WORTLAUT_PLATZHALTER ? content.split(/\n\n+/).filter(Boolean).length : 0;
  return stellen > 0 ? `${stellen} ${stellen === 1 ? "Stelle" : "Stellen"}` : "";
}

/** Ein Zeitraum des Werkzeugs („die letzten 7 Tage“, „2026-10-13“) in Worten. */
function zeitraumText(z: unknown): string {
  if (typeof z !== "string" || !z.trim()) return "";
  const t = aufnahmeZeit(z);
  return t && ISO.test(z.trim()) ? `den ${t.datum}` : z.trim();
}

/** „9 Aufnahmen“ / „1 Aufnahme“ / „nichts gefunden“ — der Titel hinter der Art. */
function anzahlTitel(n: number, eins: string, viele: string): string {
  if (n === 0) return "nichts gefunden";
  return `${n} ${n === 1 ? eins : viele}`;
}

function istFehler(r: Record<string, unknown>): boolean {
  return r.ok === false || (typeof r.error === "string" && r.error.length > 0);
}

/** Eine Liste von Aufnahmen: höchstens ALLTAG_LISTE_MAX Zeilen, dann „und N weitere“. */
function aufnahmenListe(el: HTMLElement, zeilen: ReadonlyArray<{ zeile: string; meta: string }>): void {
  const ul = liste(el);
  for (const z of zeilen.slice(0, ALLTAG_LISTE_MAX)) ul.appendChild(listenEintrag(z.zeile, undefined, z.meta || undefined));
  weitereHinweis(el, zeilen.length - ALLTAG_LISTE_MAX);
}

/** alltag_lesen — der Wortlaut (oder die Sprecher-Fassung) einer Aufnahme, oder die Liste der Aufnahmen. */
function baueWortlautKarte(r: Record<string, unknown>): HTMLElement {
  const el = baueKarte("alltag-wortlaut");
  if (istFehler(r)) {
    el.appendChild(kartenTitel(werkzeugName("alltag_lesen")));
    el.appendChild(fehlerBlock(menschlicherFehler(r, "Der Wortlaut ließ sich gerade nicht lesen.")));
    return el;
  }
  if (r.modus === "aufnahmen" || Array.isArray(r.aufnahmen)) {
    const aufnahmen = Array.isArray(r.aufnahmen) ? (r.aufnahmen as Array<Record<string, unknown>>) : [];
    const n = typeof r.count === "number" ? r.count : aufnahmen.length;
    // Die Art („Alltag-Wortlaut “) steht leise davor und verstummt in einer Faltung — der Kopf nennt sie dort.
    el.appendChild(kartenTitel(anzahlTitel(n, "Aufnahme", "Aufnahmen"), werkzeugName("alltag_lesen")));
    if (aufnahmen.length === 0) {
      const zr = zeitraumText(r.zeitraum);
      leerHinweis(el, zr ? `Keine Aufnahme mit Wortlaut für ${zr}.` : "Keine Aufnahme mit Wortlaut.");
      return el;
    }
    aufnahmenListe(
      el,
      aufnahmen.map((a) => ({
        zeile: aufnahmeZeile(aufnahmeZeit(a.aufgenommen, a.tag)),
        meta: a.nicht_lesbar === true ? "nicht lesbar" : zeichenText(a.laenge_zeichen),
      })),
    );
    if (r.abgebrochen === true) leerHinweis(el, "Die Suche brach ab – geprüft sind nur die neueren Aufnahmen.");
    return el;
  }
  // modus „lesen“: Titel „Wortlaut gelesen“, darunter EINE Zeile — nie der Text.
  const sprecher = r.fassung === "sprecher";
  if (sprecher && r.sprecher_fassung === false) {
    el.appendChild(kartenTitel("keine Sprecher-Fassung", werkzeugName("alltag_lesen")));
    leerHinweis(el, "Für diese Aufnahme gibt es keine Sprecher-Fassung.");
    return el;
  }
  el.appendChild(kartenTitel(`${sprecher ? "Sprecher-Fassung" : "Wortlaut"} gelesen`, werkzeugName("alltag_lesen")));
  const z = aufnahmeZeit(r.aufgenommen, r.tag);
  const wann = z ? `Aufnahme ${z.kurz}${z.zeit ? ` ${z.zeit}` : ""}` : "";
  const laenge = zeichenText(typeof r.laenge_zeichen === "number" ? r.laenge_zeichen : laengeVon(r.text));
  leerHinweis(el, [wann, laenge].filter(Boolean).join(" · ") || "Gelesen.");
  // Ein Stück von mehreren: die Stelle als Pille, damit „gelesen“ nicht „ganz gelesen“ heißt.
  const ab = typeof r.ab === "number" ? r.ab : 0;
  const bis = typeof r.bis === "number" ? r.bis : 0;
  if (bis > 0 && (ab > 0 || typeof r.weiter_ab === "number")) chips(el, [`Zeichen ${zahl(ab + 1)}–${zahl(bis)}`]);
  return el;
}

/** alltag_detail — Aufnahmen eines Tages oder Trefferstellen zu einem Stichwort: je Aufnahme eine Zeile. */
function baueAufnahmeKarte(r: Record<string, unknown>): HTMLElement {
  const el = baueKarte("alltag-aufnahme");
  if (istFehler(r)) {
    el.appendChild(kartenTitel(werkzeugName("alltag_detail")));
    el.appendChild(fehlerBlock(menschlicherFehler(r, "Die Aufnahmen ließen sich gerade nicht lesen.")));
    return el;
  }
  const entries = Array.isArray(r.entries) ? (r.entries as Array<Record<string, unknown>>) : [];
  const stichwort = typeof r.scannedFor === "string" && r.scannedFor.trim() ? r.scannedFor.trim() : "";
  const n = entries.length;
  el.appendChild(kartenTitel(anzahlTitel(n, "Aufnahme", "Aufnahmen"), werkzeugName("alltag_detail")));
  if (n === 0) {
    const fenster = zeitraumText(r.window);
    leerHinweis(el, `Keine Aufnahme${stichwort ? ` zu „${stichwort}“` : ""}${fenster ? ` für ${fenster}` : ""} gefunden.`);
    return el;
  }
  aufnahmenListe(
    el,
    entries.map((e) => ({ zeile: aufnahmeZeile(aufnahmeZeit(e.title, e.date)), meta: stichwort ? stellenText(e.content) : zeichenText(typeof e.zeichen_gesamt === "number" ? e.zeichen_gesamt : laengeVon(e.content)) })),
  );
  return el;
}

/** alltag_search — Tagesnotizen und Zusammenfassungen: je Tag eine Zeile, der Anriss nur bei einer Zusammenfassung. */
function baueSucheKarte(r: Record<string, unknown>): HTMLElement {
  const el = baueKarte("alltag-search");
  const entries = Array.isArray(r.entries) ? (r.entries as Array<Record<string, unknown>>) : [];
  const n = entries.length;
  el.appendChild(kartenTitel(anzahlTitel(n, "Eintrag", "Einträge"), "Mein Alltag"));
  if (n === 0) {
    leerHinweis(el, "Keine passenden Aufnahmen gefunden.");
    return el;
  }
  const ul = liste(el);
  for (const e of entries.slice(0, ALLTAG_LISTE_MAX)) {
    const titel = typeof e.title === "string" ? e.title : "";
    const roh = /Rohaufnahme/.test(titel);
    const zusammenfassung = /^Tageszusammenfassung/.test(titel);
    const z = aufnahmeZeit(e.date, titel);
    const zeile = `${z ? z.datum : "Eintrag"}${zusammenfassung ? " · Tageszusammenfassung" : roh ? " · Rohaufnahme" : ""}`;
    // Der Anriss ist die erste Zeile der ZUSAMMENFASSUNG — nie ein Rohtranskript, nie der Platzhalter des Verlaufs.
    let anriss = "";
    if (!roh && typeof e.content === "string" && e.content !== WORTLAUT_PLATZHALTER) {
      anriss =
        e.content
          .replace(/<!--[\s\S]*?-->/g, "")
          .split("\n")
          .map((l) => l.trim())
          .find((l) => l.length > 0 && !l.startsWith("#") && !l.startsWith(">") && !/^_[^_]+_$/.test(l)) ?? "";
      if (anriss.length > 160) anriss = `${anriss.slice(0, 159).trimEnd()}…`;
    }
    ul.appendChild(listenEintrag(zeile, anriss || undefined));
  }
  weitereHinweis(el, n - ALLTAG_LISTE_MAX);
  return el;
}

/** Die eine Tür: Werkzeugname rein, Karte raus. */
export function baueAlltagKarte(tool: string, result: unknown): HTMLElement {
  const r = auspacken(result);
  if (tool === "alltag_lesen") return baueWortlautKarte(r);
  if (tool === "alltag_detail") return baueAufnahmeKarte(r);
  return baueSucheKarte(r);
}
